import { lastmodFor } from "@/lib/route-lastmod";
import type { ReactNode } from "react";
import { TelehealthCta } from "@/components/site/TelehealthCta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { workTranslationOf } from "@/lib/medical-schema";
import { faqPageJson } from "@/lib/faq-schema";
import { PHYSICIAN_REF, imageRights } from "@/lib/medical-schema";
import gastricBypassDiagram from "@/assets/gastric-bypass-diagram.png.asset.json";
import sleeveGastrectomy from "@/assets/sleeve-gastrectomy.jpg.asset.json";
import bypassWeightLossGraph from "@/assets/bypass-weight-loss-graph.png.asset.json";
import sleeveWeightLossGraph from "@/assets/sleeve-excess-weight-loss-percentage-graph.png.asset.json";
import sleeveGraphWebp from "@/assets/sleeve-excess-weight-loss-percentage-graph.webp.asset.json";
import sleeveGraphWebp720 from "@/assets/sleeve-excess-weight-loss-percentage-graph-720.webp.asset.json";
import { gastricDiagramResponsive, bypassGraphResponsive } from "@/lib/service-responsive";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { InlineAppointmentCTA } from "@/components/site/InlineAppointmentCTA";
import { MedicalReviewBanner } from "@/components/site/MedicalReview";
import { schemaDateFields } from "@/lib/schema-dates";

const URL = "https://virginiabariatric.com/services/bypass-vs-sleeve";
const TITLE = "Gastric Bypass vs. Sleeve: Which Is Better? | Virginia Bariatric";
/** Clinical review date for this page's content model — drives visible copy and JSON-LD. */
const REVIEWED_ISO = lastmodFor("/services/bypass-vs-sleeve") ?? "2026-08-09";
const DESCRIPTION =
  "Gastric bypass vs. sleeve gastrectomy compared by weight loss, diabetes remission, reflux, and safety — with Dr. Fitzer's own 10-year patient data.";

const FAQ = [
  {
    q: "Is gastric bypass or sleeve gastrectomy better?",
    a: "Neither is better for everybody — they are different tools. Bypass produces modestly greater long-term weight loss and better diabetes remission and reliably improves reflux. The sleeve is a shorter, simpler operation that leaves the intestines untouched. The right choice depends on your health history, medications, and reflux.",
  },
  {
    q: "How much weight will I lose with bypass versus sleeve?",
    a: "On average, bypass patients lose about 70 to 79% of excess body weight and sleeve patients about 67 to 79%. The ranges reflect Dr. Fitzer's MBSAQIP patient follow-up data. Through the first few years the two perform similarly; the gap opens after four or five years. At Virginia Bariatric Surgery, bypass patients average around 70% excess weight loss and sleeve patients over 70% through year nine.",
  },
  {
    q: "Which operation is better for reflux?",
    a: "Gastric bypass. It is the most reliable anti-reflux operation in bariatric surgery. A sleeve can worsen existing reflux, and 10–20% of patients without reflux develop it after a sleeve. Repairing a hiatal hernia at the time of sleeve surgery makes post-op reflux less likely, but it still can happen.",
    node: (
      <>
        Gastric bypass. It is the most reliable anti-reflux operation in bariatric surgery. A sleeve can worsen existing reflux, and 10–20% of patients without reflux develop it after a sleeve.{" "}
        <Link to="/services/hiatal-hernia-surgery" className="text-brand-deep underline hover:no-underline">Repairing a hiatal hernia</Link> at the time of sleeve surgery makes post-op reflux less likely, but it still can happen.
      </>
    ),
  },
  {
    q: "Which operation is better for type 2 diabetes?",
    a: "Both improve blood sugar dramatically, often before meaningful weight loss. Bypass edges out the sleeve: in a controlled Norwegian trial, half of bypass patients had an HbA1c under 6.0% five years later, compared with a fifth of sleeve patients.",
  },
  {
    q: "Is a gastric sleeve or gastric bypass reversible?",
    a: "A sleeve removes most of the stomach and is permanent. A bypass can technically be reconstructed but rarely is. A sleeve can, however, be converted to a bypass later if reflux or weight regain becomes a problem.",
  },
];

const COMPARE_IMAGE_RIGHTS = imageRights("https://virginiabariatric.com/services/bypass-vs-sleeve");

export const Route = createFileRoute("/services/bypass-vs-sleeve")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: `https://virginiabariatric.com${bypassWeightLossGraph.url}` },
        {
        property: "og:image:alt",
        content: "Excess weight loss percentage after Roux-en-Y gastric bypass at Virginia Bariatric Surgery, from 1 month through 10 years post-surgery",
      },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: `https://virginiabariatric.com${bypassWeightLossGraph.url}` },
        {
        name: "twitter:image:alt",
        content: "Excess weight loss percentage after Roux-en-Y gastric bypass at Virginia Bariatric Surgery, from 1 month through 10 years post-surgery",
      },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/services/bypass-vs-sleeve") : []),
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@graph": [
            // BreadcrumbList is emitted sitewide by __root.tsx — do not duplicate it here.

            {
              "@type": "MedicalWebPage",
              ...schemaDateFields("/services/bypass-vs-sleeve", REVIEWED_ISO),
              "@id": `${URL}#webpage`,
              ...workTranslationOf("/services/bypass-vs-sleeve"),
              reviewedBy: PHYSICIAN_REF,
              name: "Gastric Bypass or Gastric Sleeve? Comparing Two Popular Options",
              description: DESCRIPTION,
              url: URL,
              inLanguage: "en-US",
              specialty: "Bariatric",
              about: [
                { "@type": "MedicalProcedure", name: "Roux-en-Y Gastric Bypass", url: "https://virginiabariatric.com/services/bypass" },
                { "@type": "MedicalProcedure", name: "Sleeve Gastrectomy", url: "https://virginiabariatric.com/services/sleeve" },
              ],
              image: [
                {
                  "@type": "ImageObject",
                  contentUrl: `https://virginiabariatric.com${bypassWeightLossGraph.url}`,
                  url: `https://virginiabariatric.com${bypassWeightLossGraph.url}`,
                  caption:
                    "Excess weight loss percentage after Roux-en-Y gastric bypass at Virginia Bariatric Surgery, from 1 month through 10 years post-surgery",
                  name: "Roux-en-Y gastric bypass 10-year excess weight loss chart",
                  width: 1664,
                  height: 652,
                  inLanguage: "en-US",
                  ...COMPARE_IMAGE_RIGHTS,
                },
                {
                  "@type": "ImageObject",
                  contentUrl: `https://virginiabariatric.com${sleeveWeightLossGraph.url}`,
                  url: `https://virginiabariatric.com${sleeveWeightLossGraph.url}`,
                  caption:
                    "Average excess weight loss percentage after laparoscopic sleeve gastrectomy at Virginia Bariatric Surgery, from 1 month through 10 years",
                  name: "Sleeve gastrectomy 10-year excess weight loss chart",
                  width: 1920,
                  height: 780,
                  inLanguage: "en-US",
                  ...COMPARE_IMAGE_RIGHTS,
                },
              ],
              publisher: {
                "@type": "MedicalOrganization",
                name: "Virginia Bariatric Surgery",
                url: "https://virginiabariatric.com",
              },
            },
            {
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ] : [],
  }),
  component: BypassVsSleevePage,
});

const ROWS: { label: string; sleeve: string; bypass: string }[] = [
  {
    label: "What happens",
    sleeve: "About 75–80% of the stomach is removed, leaving a narrow tube. Intestines are untouched.",
    bypass: "A small pouch is made and connected directly to the small intestine, bypassing the rest (90–95%) of the stomach and the duodenum.",
  },
  { label: "% Excess body weight loss.*", sleeve: "67 to 79%", bypass: "70 to 79%" },
  {
    label: "Diabetes",
    sleeve: "Strong improvement; remission in many patients",
    bypass: "Somewhat higher and more durable remission rates",
  },
  {
    label: "Reflux (GERD)",
    sleeve: "Can worsen existing reflux or cause new reflux",
    bypass: "Usually improves reflux; the preferred option if GERD is a problem",
  },
  { label: "Operating time", sleeve: "Roughly 45 minutes, one staple line", bypass: "Roughly 1 hour, two connections" },
  { label: "Vitamins", sleeve: "Lifelong supplementation", bypass: "Lifelong supplementation" },
  {
    label: "Main long-term risks",
    sleeve: "Reflux, sleeve dilation, weight regain",
    bypass: "Marginal ulcer, dumping syndrome, nutrient deficiency, internal hernia",
  },
];

function BypassVsSleevePage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif text-center">
          Gastric Bypass or Gastric Sleeve? Comparing Two Popular Options
        </h1>
      </header>

      <MedicalReviewBanner className="mt-4" />

      <p className="mt-4 text-foreground/80">
        Every bariatric surgery success story begins with someone making a decision about which operation they're going to get.&nbsp; More often than not, the decision comes down to choosing between the bypass and sleeve.&nbsp;
      </p>

      <TelehealthCta />
      <p className="mt-3 text-foreground/80">
        If you are trying to figure out which one is better, the honest answer is it depends who's asking. The two operations are extremely similar, and they are essentially equivalent for most patients. Most people would do equally well with either one.
      </p>
      <p className="mt-3 text-foreground/80">
        There are some cases where one procedure should be favored over the other. Dr. Fitzer will help you understand whether and how your own health status should be factored into the decision making.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        How do the bypass and sleeve actually make you lose weight?
      </h2>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div>
          <p className="text-foreground/80">
            During a{" "}
            <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">
              sleeve gastrectomy
            </Link>
            , the surgeon uses a surgical stapling device to remove roughly 75 to 80 percent of the
            stomach. What remains is a sealed, tube-shaped stomach that is pretty close to the size
            and shape of a standard banana.
          </p>
          <p className="mt-3 text-foreground/80">
            Nothing is rerouted. The intestines aren’t touched. You eat less because the new stomach
            holds less, and you feel less hungry because the part that came out is what manufactured
            most of your body’s ghrelin, a hunger hormone.
          </p>
        </div>
        <figure className="flex flex-col items-center">
          <img
            src={sleeveGastrectomy.url}
            alt="Illustration of a sleeve gastrectomy showing the banana-shaped stomach tube left after 80% of the stomach is removed"
            width={574}
            height={725}
            loading="lazy"
            decoding="async"
            className="w-2/3 md:w-full max-w-[14rem] h-auto rounded-lg border border-border"
          />
          <figcaption className="mt-2 text-center text-sm text-foreground/70">
            Sleeve gastrectomy: a narrow, banana-shaped stomach tube
          </figcaption>
        </figure>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div>
          <p className="text-foreground/80">
            In a{" "}
            <Link to="/services/bypass" className="text-brand-deep underline hover:no-underline">
              gastric bypass
            </Link>
            , the surgeon uses the same stapler to create a small stomach pouch about the size of
            an egg. The pouch is then connected directly to the small intestine. After surgery, food
            “bypasses” the larger part of the stomach, plus a small stretch of intestine.
          </p>
          <p className="mt-3 text-foreground/80">
            The pouch is the part that is critical for weight loss. Like the tubular stomach you get
            with a sleeve,<sup className="text-xs">[1]</sup> a small bypass pouch limits how much you
            eat by sending nerve signals to your brain. It also sends a hormonal signal to the brain
            and pancreas, the end result of which is a sensation of fullness and better blood sugar
            control.<sup className="text-xs">[2]</sup>
          </p>
        </div>
        <figure className="flex flex-col items-center">
          <img
            src={gastricDiagramResponsive.src}
            srcSet={gastricDiagramResponsive.srcSet}
            sizes="(min-width: 768px) 224px, min(66vw, 224px)"
            alt="Medical diagram of Roux-en-Y gastric bypass anatomy showing the small pouch and rerouted small intestine"
            width={406}
            height={610}
            loading="lazy"
            decoding="async"
            className="w-2/3 md:w-full max-w-[14rem] h-auto rounded-lg border border-border"
          />
          <figcaption className="mt-2 text-center text-sm text-foreground/70">
            Schematic of the Roux-en-Y gastric bypass
          </figcaption>
        </figure>
      </div>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        How gastric bypass and sleeve gastrectomy compare, side by side
      </h2>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-left text-[0.95rem]">
          <caption className="sr-only">
            Comparison of sleeve gastrectomy and gastric bypass by anatomy, weight loss, diabetes,
            reflux, operating time, vitamins, and long-term risks
          </caption>
          <thead>
            <tr className="bg-brand-soft/50">
              <th scope="col" className="border border-border p-3 font-serif" />
              <th scope="col" className="border border-border p-3 font-serif">
                Sleeve gastrectomy
              </th>
              <th scope="col" className="border border-border p-3 font-serif">
                Gastric bypass
              </th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => {
              const hasAsterisk = r.label.endsWith("*");
              const labelText = hasAsterisk ? r.label.slice(0, -1) : r.label;
              return (
                <tr key={r.label} className="align-top">
                  <th scope="row" className="border border-border p-3 font-semibold text-foreground">
                    {labelText}
                    {hasAsterisk && <sup className="text-xs">*</sup>}
                  </th>
                  <td className="border border-border p-3 text-foreground/80">{r.sleeve}</td>
                  <td className="border border-border p-3 text-foreground/80">{r.bypass}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-sm text-foreground/70">
        <sup className="text-xs">*</sup> The ranges reflect Dr. Fitzer&apos;s MBSAQIP patient follow-up data.
      </p>

      <InlineAppointmentCTA
        heading="Still deciding between bypass and sleeve?"
        body="The comparison helps, but the right answer is individual. Dr. Fitzer will walk through your reflux, diabetes, medications, and goals to recommend the best operation for you."
        buttonText="Schedule an Appointment to discuss bypass vs. sleeve surgery"
        secondaryButton={{ text: "Check Bariatric Surgery Insurance Coverage", to: "/resources/insurance-coverage" }}
      />

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        How does weight loss hold up in the longer term?
      </h2>
      <p className="mt-3 text-foreground/80">
        If you peruse the relevant medical literature, you will see variations of the following.
        Through the first few years, the two procedures usually perform similarly. The gap opens up
        after four or five years.
      </p>
      <p className="mt-3 text-foreground/80">
        In long-term randomized trials that followed patients for a full decade, bypass patients had
        modestly greater weight loss than sleeve patients. For example, the difference was 65 percent versus 61 percent of excess BMI lost in one Swiss trial. The sleeve patients were more likely to need a second operation for inadequate weight loss.
      </p>
      <p className="mt-3 text-foreground/80">
        Here is how Dr. Fitzer’s own patients have done in real life. The first chart shows ten-year excess weight
        loss after gastric bypass; the second shows the same measure after sleeve gastrectomy.
      </p>

      <figure className="mt-4">
        <picture>
        <source srcSet={bypassGraphResponsive.srcSet} sizes="(min-width: 1024px) 896px, calc(100vw - 3rem)" type="image/webp" />
        <img
          src={bypassGraphResponsive.src}
          alt="Line chart of gastric bypass results: excess weight loss climbs to roughly 79% at one year and holds near 70% through year ten"
          width={1664}
          height={652}
          loading="lazy"
          decoding="async"
          sizes="(min-width: 1024px) 896px, calc(100vw - 3rem)"
          className="w-full h-auto rounded-lg border border-border"
        />
        </picture>
        <figcaption className="mt-2 text-center text-sm text-muted-foreground">
          Excess weight loss percentage after Roux-en-Y gastric bypass at Virginia Bariatric Surgery,
          from 1 month through 10 years post-surgery.
        </figcaption>
      </figure>

      <figure className="mt-6">
        <picture>
        <source srcSet={`${sleeveGraphWebp720.url} 720w, ${sleeveGraphWebp.url} 1920w`} sizes="(min-width: 1024px) 896px, calc(100vw - 3rem)" type="image/webp" />
        <img
          src={sleeveWeightLossGraph.url}
          srcSet={`${sleeveWeightLossGraph.url} 1920w`}
          sizes="(min-width: 1024px) 896px, calc(100vw - 3rem)"
          alt="Line chart of sleeve gastrectomy results: excess weight loss peaks near 79% and stays above 67% from year one through year ten"
          width={1920}
          height={780}
          loading="lazy"
          decoding="async"
          className="block w-full h-auto rounded-lg"
        />
        </picture>
        <figcaption className="mt-2 text-center text-sm text-muted-foreground">
          Average excess weight loss percentage after laparoscopic sleeve gastrectomy at Virginia Bariatric Surgery, from 1 month through 10 years.
        </figcaption>
      </figure>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Type 2 diabetes: bypass edges out the sleeve
      </h2>
      <p className="mt-3 text-foreground/80">
        Both procedures improve blood sugar dramatically, often right away, before meaningful weight
        loss has occurred. Bypass tends to edge out the sleeve in diabetes control. In a carefully
        controlled Norwegian trial, half of bypass patients had an HbA1c under 6.0 percent five years
        later, compared with a fifth of sleeve patients.
      </p>
      <p className="mt-3 text-foreground/80">
        Surgeons often lean toward bypass for patients who have had diabetes for many years, who use
        insulin, or whose blood sugar has been difficult to control. Remission is not the same as
        cure — diabetes can return, especially with weight regain — but many people spend years or
        decades off medication. Some never return to it.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Reflux: a clear difference between the two operations
      </h2>
      <p className="mt-3 text-foreground/80">
        This is where the two operations genuinely differ, and it’s a factor that is often
        underweighted by patients: the sleeve has a bit of a reflux problem. If you have reflux,
        getting a sleeve often worsens it. For people without reflux, a sleeve will install reflux
        bad enough to require a daily pill in some 10 to 20 percent of them. Occasionally the reflux
        is quite severe. Around 5 percent of sleeve patients eventually seek conversion to a bypass
        specifically for relief from heartburn.
      </p>
      <p className="mt-3 text-foreground/80">
        Gastric bypass, by contrast, is the most reliable anti-reflux operation in bariatric surgery.
        Improved or resolved heartburn is the norm.
      </p>
      <p className="mt-3 text-foreground/80">
        If you already take a daily proton pump inhibitor (any heartburn medicine ending in
        “-prazole”), or <Link to="/services/hiatal-hernia-surgery" className="text-brand-deep underline hover:no-underline">have a hiatal hernia</Link>, or have been diagnosed with Barrett’s esophagus, expect
        your surgeon to nudge you toward bypass. If reflux has never been an issue for you, this
        consideration carries much less weight.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        How safe are gastric bypass and sleeve gastrectomy?
      </h2>
      <p className="mt-3 text-foreground/80">
        Both operations are very safe. In the large, national databases, thirty-day mortality
        averages one to two patients in a thousand. That figure is on par with gallbladder surgery, and it's lower than hip replacement. Those same databases put serious complication rates at around one percent for the sleeve and modestly higher for the bypass.
      </p>

      <p className="mt-3 text-foreground/80">
        Each has its own signature risks: staple line leak and stricture for the sleeve; marginal
        ulcer and internal hernia for the bypass, the latter of which can appear years down the road.
        It’s why bypass patients are taught not to delay medical evaluation for abdominal pain.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Living in harmony with your chosen procedure
      </h2>
      <p className="mt-3 text-foreground/80">
        Surgery is a tool, not a finish line. Both operations commit you to lifelong habits, but the
        specifics differ:
      </p>
      <ul className="mt-4 list-disc pl-6 space-y-2 text-foreground/80">
        <li>
          <strong>Vitamins.</strong> Both require daily supplements for life: B12, iron, calcium
          citrate, vitamin D, and a bariatric multivitamin. We periodically check your blood work to
          make sure you're getting what you need.
          <p className="mt-3 text-foreground/80">
            Most patients eventually notice that when they skip a day or a week of supplements, they
            feel no real difference. Don't be fooled! It's not that the supplements aren't helping
            you; deficiencies just develop slowly. Deficiency symptoms can take years to show up. In
            rare cases, the consequences of a deficiency can be permanent.
          </p>
        </li>
        <li>
          <strong>Medications.</strong> After bypass, over-the-counter NSAIDs such as ibuprofen and
          naproxen are generally off-limits because of ulcer risk. There may be a minor increase in
          ulcer risk in sleeve patients, but once they are several months out from surgery, most can
          take NSAIDs without any problems.
          <p className="mt-3 text-foreground/80">
            The sleeve leaves the intestinal route intact, which matters if you take drugs with
            narrow dosing windows, such as transplant medications.
          </p>
          <p className="mt-3 text-foreground/80">
            Absorption is also affected. A few medicines have been observed to have modestly reduced
            absorption after gastric bypass.
          </p>
        </li>
        <li>
          <strong>Eating.</strong> Bypass patients may experience dumping syndrome — cramping,
          flushing, and diarrhea after sugary or fatty foods. Unpleasant, but many people find it a
          useful guardrail. Sleeve patients rarely get this.
        </li>
        <li>
          <strong>Alcohol.</strong> Both procedures raise blood alcohol faster; bypass carries a
          modest increase in risk of{" "}
          <Link
            to="/dr-fitzer-dissects-the-news/$slug"
            params={{ slug: "why-are-gastric-bypass-patients-at-risk-for-alcoholism" }}
            className="text-brand-deep underline hover:no-underline"
          >
            alcohol use disorder
          </Link>
          .
        </li>
        <li>
          <strong>Weight regain.</strong> Minor regain after the two-year mark is normal and
          expected. Meaningful regain affects a minority. Attending follow-up appointments is one of
          the strongest predictors of keeping the weight off, and modern{" "}
          <Link to="/medical-weight-loss/medications" className="text-brand-deep underline hover:no-underline">
            weight-loss medications
          </Link>{" "}
          now give clinicians an option if regain occurs. See our guide to{" "}
          <Link to="/resources/managing-weight-regain-after-bariatric-surgery" preload="intent" className="text-brand-deep underline hover:no-underline">
            managing weight regain after bariatric surgery
          </Link>
          .
        </li>
      </ul>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Which operation is reversible — the sleeve or the bypass?
      </h2>
      <p className="mt-3 text-foreground/80">
        A sleeve operation entails removal of most of the stomach, which is an organ we can’t “save
        for later.” It’s permanent. A bypass can technically be reconstructed or “reversed,” but
        rarely is. The one useful asymmetry: a sleeve can be converted to a bypass later if reflux or
        weight regain becomes a problem, and this is a well-established path —{" "}
        <Link to="/services/revisions" className="text-brand-deep underline hover:no-underline">
          see our page on revisional bariatric surgery
        </Link>
        . A bypass has only reversal.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Questions worth bringing to your consultation
      </h2>
      <p className="mt-3 text-foreground/80">
        Ask your surgeon how often they perform each operation and what their own complication and
        revision rates look like. Ask specifically how your reflux history, your diabetes, your
        current medications, and any prior abdominal surgery point toward one option.
      </p>
      <p className="mt-3 text-foreground/80">
        Ask what the follow-up schedule looks like at five and ten years, not just at six weeks. The availability of long-term support is as important as the operation you choose.
      </p>
      <p className="mt-3 text-foreground/80">
        Cost can also shape the decision if insurance will not cover the operation. Our{" "}
        <Link to="/self-pay-bariatric-surgery" className="text-brand-deep underline hover:no-underline">
          self-pay bariatric surgery
        </Link>{" "}
        page lists our published prices for both procedures.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Deciding between bypass and sleeve in Northern Virginia</h2>
      <p className="mt-3 text-foreground/80">Both operations are performed at INOVA Fair Oaks Hospital, and both typically involve one night in the hospital, so location and recovery logistics are the same either way. The decision comes down to reflux, diabetes and your goals, which Dr. Fitzer reviews with you at our Herndon office or by telehealth.</p>
      <p className="mt-3 text-foreground/80">Learn more about <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery in Northern Virginia</Link> and <Link to="/services/bypass" className="text-brand-deep underline hover:no-underline">gastric bypass in Northern Virginia</Link>.</p>

      <h2 id="faq" className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Frequently Asked Questions
      </h2>
      <dl className="mt-2 space-y-3 text-foreground/80">
        {FAQ.map((f) => (
          <div key={f.q}>
            <dt className="font-medium text-foreground">{f.q}</dt>
            <dd className="mt-1">{("node" in f ? f.node : f.a) as ReactNode}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/appointments"
          className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Request a consultation
        </Link>
        <WeightLossOptionsButtons />
      </div>

      <p className="mt-8 text-sm text-foreground/70">
        This article is general education and is not medical advice. Decisions about weight-loss
        surgery should be made with a qualified bariatric surgeon and care team who know your full
        medical history.
      </p>

      <div className="mt-6 space-y-3 text-sm text-foreground/70">
        <p>
          <sup className="text-brand">[1]</sup> It’s helpful to think of a sleeve as a pouch. It’s just
          a long, narrow pouch.
        </p>
        <p>
          <sup className="text-brand">[2]</sup> It’s commonly assumed that gastric bypass surgery also
          reduces calorie absorption, but that is not really the case. In the modern era, the bypass
          is performed in such a way that malabsorption of calories is negligible. There is some
          malabsorption of certain nutrients, but malabsorption of calories is not enough to
          contribute to weight loss. It is a more involved operation, with two surgical connections
          instead of one staple line.
        </p>
      </div>
      <LastUpdated className="mt-10" />
      </article>
  );
}
